import { useId, useState } from "react";

import { cleanSiren, sirenStatus } from "../lib/siren";

interface Props {
  value: string;
  onChange: (siren: string) => void;
  label: string;
  hint?: string;
  error?: string | null;
  size?: "large" | "compact";
  autoFocus?: boolean;
  id?: string;
}

/**
 * Nine boxed cells grouped 3-3-3, like an administrative form.
 * A single transparent <input> sits on top so paste, autofill and screen readers
 * behave like a normal text field.
 */
export function SirenField({ value, onChange, label, hint, error, size = "large", autoFocus, id }: Props) {
  const autoId = useId();
  const inputId = id ?? `siren-${autoId}`;
  const [focused, setFocused] = useState(false);
  const status = sirenStatus(value);
  const invalid = status === "invalid";
  const message = error ?? (invalid ? "Ce numéro n'est pas un SIREN valide : vérifiez les chiffres." : null);

  const describedBy = [hint && `${inputId}-hint`, message && `${inputId}-error`].filter(Boolean).join(" ");
  const caret = Math.min(value.length, 8);

  const classes = [
    "siren-field",
    `siren-field--${size}`,
    focused && "is-focused",
    status === "valid" && !error && "is-valid",
    (invalid || error) && "is-invalid",
  ].filter(Boolean);

  return (
    <div className="field">
      <label className="field__label" htmlFor={inputId}>
        {label}
      </label>
      {hint && (
        <span className="field__hint" id={`${inputId}-hint`}>
          {hint}
        </span>
      )}
      <div className={classes.join(" ")}>
        <div className="siren-field__cells" aria-hidden="true">
          {Array.from({ length: 9 }, (_, i) => (
            <span
              key={i}
              className={[
                "siren-field__cell",
                i > 0 && i % 3 === 0 && "siren-field__cell--group",
                value[i] && "siren-field__cell--filled",
                i === caret && "siren-field__cell--caret",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {value[i] ?? ""}
            </span>
          ))}
        </div>
        <input
          id={inputId}
          className="siren-field__input"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          value={value}
          aria-invalid={invalid || Boolean(error)}
          aria-describedby={describedBy || undefined}
          onChange={(e) => onChange(cleanSiren(e.target.value))}
          onFocus={(e) => {
            setFocused(true);
            const end = e.target.value.length;
            e.target.setSelectionRange(end, end);
          }}
          onSelect={(e) => {
            // Editing happens at the end only, which keeps the caret cell truthful.
            const input = e.currentTarget;
            const end = input.value.length;
            if (input.selectionStart !== end || input.selectionEnd !== end) input.setSelectionRange(end, end);
          }}
          onBlur={() => setFocused(false)}
        />
      </div>
      {message && (
        <span className="field__error" id={`${inputId}-error`} role="alert">
          {message}
        </span>
      )}
    </div>
  );
}
